import React from 'react';
import './App.css';
import UniversalHeader from './UniversalHeader';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';

function occupancyLabel(status) {
  switch (status) {
    case 'high':
      return 'High (90%+)';
    case 'medium':
      return 'Stable (75–90%)';
    case 'low':
      return 'Needs attention';
    default:
      return status || '—';
  }
}

function Properties() {
  const [search, setSearch] = React.useState('');
  const [properties, setProperties] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [listError, setListError] = React.useState('');
  const navigate = useNavigate();

  const loadProperties = React.useCallback(async () => {
    if (!supabase) {
      setListError('Supabase is not configured.');
      setProperties([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setListError('');
    const { data, error } = await supabase
      .from('properties')
      .select('id, name, location, room_count, occupancy_status')
      .order('created_at', { ascending: false });

    if (error) {
      setListError(error.message);
      setProperties([]);
    } else {
      setProperties(
        (data || []).map((row) => ({
          id: row.id,
          name: row.name,
          location: row.location || '—',
          rooms: row.room_count,
          status: row.occupancy_status,
          occupancy: occupancyLabel(row.occupancy_status),
        }))
      );
    }
    setLoading(false);
  }, []);

  React.useEffect(() => {
    loadProperties();
  }, [loadProperties]);

  const [detailId, setDetailId] = React.useState(null);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [detailError, setDetailError] = React.useState('');
  const [detailData, setDetailData] = React.useState(null);

  React.useEffect(() => {
    if (!detailId) return undefined;

    if (!supabase) {
      setDetailLoading(false);
      setDetailError('Supabase is not configured.');
      setDetailData(null);
      return undefined;
    }

    let cancelled = false;
    setDetailLoading(true);
    setDetailError('');
    setDetailData(null);

    (async () => {
      const [propRes, roomsRes, galRes] = await Promise.all([
        supabase.from('properties').select('*').eq('id', detailId).single(),
        supabase
          .from('property_rooms')
          .select('*')
          .eq('property_id', detailId)
          .order('sort_order', { ascending: true }),
        supabase
          .from('property_gallery_images')
          .select('*')
          .eq('property_id', detailId)
          .order('sort_order', { ascending: true }),
      ]);

      if (cancelled) return;

      if (propRes.error) {
        setDetailError(propRes.error.message);
      } else {
        setDetailData({
          property: propRes.data,
          rooms: roomsRes.data || [],
          gallery: galRes.data || [],
        });
      }
      setDetailLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [detailId]);

  React.useEffect(() => {
    if (!detailId) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setDetailId(null);
        setDetailData(null);
        setDetailError('');
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [detailId]);

  const closeDetailModal = () => {
    setDetailId(null);
    setDetailData(null);
    setDetailError('');
  };

  const openDetailModal = (property) => {
    if (!supabase) {
      // eslint-disable-next-line no-alert
      alert('Supabase is not configured.');
      return;
    }
    setDetailId(property.id);
  };

  const handleEdit = (property) => {
    navigate(`/properties/${property.id}/edit`);
  };

  const handleDelete = async (property) => {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      `Are you sure you want to delete "${property.name}"? This cannot be undone.`
    );
    if (!ok) return;
    if (!supabase) {
      // eslint-disable-next-line no-alert
      alert('Supabase is not configured.');
      return;
    }
    const { error } = await supabase
      .from('properties')
      .delete()
      .eq('id', property.id);
    if (error) {
      // eslint-disable-next-line no-alert
      alert(error.message);
      return;
    }
    await loadProperties();
  };

  const filtered = properties.filter((p) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      p.name.toLowerCase().includes(q) || p.location.toLowerCase().includes(q)
    );
  });

  return (
    <div className="properties-root">
      <UniversalHeader />

      <div className="properties-shell">
        <header className="properties-header">
          <div>
            <h1 className="properties-title">Properties</h1>
            <p className="properties-subtitle">
              Manage buildings, rooms, and documents from one view.
            </p>
          </div>

          <div className="properties-actions">
            <button
              type="button"
              className="btn-primary properties-btn"
              onClick={() => navigate('/properties/new')}
            >
              + Add property
            </button>
          </div>
        </header>

        {listError ? (
          <p className="properties-save-error" role="alert">
            {listError}
          </p>
        ) : null}

        <section className="properties-toolbar properties-toolbar--search-only">
          <div className="properties-search">
            <div className="input-shell properties-search-shell">
              <div className="input-icon">🔍</div>
              <input
                className="input-element"
                type="text"
                placeholder="Search by name or location"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </section>

        <main className="properties-layout properties-layout--single">
          <section className="properties-list">
            <div className="properties-list-header">
              <span className="properties-list-label">Properties</span>
              <span className="properties-list-count">
                {loading ? '…' : `${filtered.length} total`}
              </span>
            </div>

            {loading ? (
              <p className="properties-list-empty">Loading properties…</p>
            ) : (
              <div className="properties-grid">
                {filtered.length === 0 ? (
                  <p className="properties-list-empty">
                    No properties yet. Use “+ Add property” above.
                  </p>
                ) : (
                  filtered.map((p) => (
                    <article className="properties-card" key={p.id}>
                      <div className="properties-card-main">
                        <div>
                          <h2 className="properties-card-title">{p.name}</h2>
                          <p className="properties-card-location">{p.location}</p>
                        </div>
                        <span
                          className={`properties-status properties-status--${p.status}`}
                        >
                          {p.occupancy}
                        </span>
                      </div>

                      <div className="properties-card-meta">
                        <span>
                          {typeof p.rooms === 'number'
                            ? `${p.rooms} rooms`
                            : 'Room count not set'}
                        </span>
                        <span>Residential</span>
                      </div>

                      <div className="properties-card-actions">
                        <button
                          type="button"
                          className="properties-icon-btn"
                          aria-label="View property details"
                          onClick={() => openDetailModal(p)}
                        >
                          👁
                        </button>
                        <button
                          type="button"
                          className="properties-icon-btn"
                          aria-label="Edit property"
                          onClick={() => handleEdit(p)}
                        >
                          ✏️
                        </button>
                        <button
                          type="button"
                          className="properties-icon-btn properties-icon-btn--danger"
                          aria-label="Delete property"
                          onClick={() => handleDelete(p)}
                        >
                          🗑
                        </button>
                      </div>
                    </article>
                  ))
                )}
              </div>
            )}
          </section>
        </main>
      </div>

      {detailId ? (
        <div
          className="properties-modal-root"
          role="presentation"
          onClick={closeDetailModal}
        >
          <div
            className="properties-modal-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="property-detail-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="properties-modal-header">
              <h2 id="property-detail-title" className="properties-modal-title">
                Property details
              </h2>
              <button
                type="button"
                className="properties-modal-close"
                aria-label="Close"
                onClick={closeDetailModal}
              >
                ×
              </button>
            </div>

            <div className="properties-modal-body">
              {detailLoading ? (
                <p className="properties-list-empty">Loading…</p>
              ) : null}
              {detailError ? (
                <p className="properties-save-error" role="alert">
                  {detailError}
                </p>
              ) : null}
              {!detailLoading && detailData?.property ? (
                <>
                  <div className="properties-modal-section">
                    <h3 className="properties-modal-label">Name</h3>
                    <p className="properties-modal-value">
                      {detailData.property.name}
                    </p>
                  </div>
                  <div className="properties-modal-section">
                    <h3 className="properties-modal-label">Location</h3>
                    <p className="properties-modal-value">
                      {detailData.property.location || '—'}
                    </p>
                  </div>
                  <div className="properties-modal-grid">
                    <div className="properties-modal-section">
                      <h3 className="properties-modal-label">Price (£ / month)</h3>
                      <p className="properties-modal-value">
                        {detailData.property.price_per_month != null
                          ? `£${detailData.property.price_per_month}`
                          : '—'}
                      </p>
                    </div>
                    <div className="properties-modal-section">
                      <h3 className="properties-modal-label">Room count</h3>
                      <p className="properties-modal-value">
                        {typeof detailData.property.room_count === 'number'
                          ? detailData.property.room_count
                          : '—'}
                      </p>
                    </div>
                    <div className="properties-modal-section">
                      <h3 className="properties-modal-label">Occupancy</h3>
                      <p className="properties-modal-value">
                        {occupancyLabel(detailData.property.occupancy_status)}
                      </p>
                    </div>
                  </div>

                  {detailData.property.main_image_url ? (
                    <div className="properties-modal-section">
                      <h3 className="properties-modal-label">Main image</h3>
                      <img
                        src={detailData.property.main_image_url}
                        alt="Main listing"
                        className="properties-modal-main-img"
                      />
                    </div>
                  ) : null}

                  {detailData.gallery.length > 0 ? (
                    <div className="properties-modal-section">
                      <h3 className="properties-modal-label">Gallery</h3>
                      <ul className="properties-modal-gallery">
                        {detailData.gallery.map((img, idx) => (
                          <li key={img.id}>
                            <img
                              src={img.image_url}
                              alt={`Gallery ${idx + 1}`}
                            />
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}

                  {detailData.rooms.length > 0 ? (
                    <div className="properties-modal-section">
                      <h3 className="properties-modal-label">Rooms</h3>
                      <ul className="properties-modal-rooms">
                        {detailData.rooms.map((room) => (
                          <li key={room.id}>
                            <span className="properties-modal-room-name">
                              {room.room_name || '—'}
                            </span>
                            <span className="properties-modal-room-size">
                              {room.size_sqm != null ? `${room.size_sqm} sqm` : ''}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>

            <div className="properties-modal-footer">
              <button
                type="button"
                className="btn-primary properties-btn"
                onClick={closeDetailModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default Properties;
